import React, {useState, useCallback, useEffect} from "react";
import {DateRange} from "react-date-range";
import ModalDefault from "../../../common/modal/modal-default";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export interface ICustomDateRangeModalProps {
    isOpen: boolean;
    initialStartDate?: Date;
    initialEndDate?: Date;
    onClose: () => void;
    onApply: (startDate: Date, endDate: Date) => void;
    onCancel?: () => void;
}

const CustomDateRangeModal: React.FC<ICustomDateRangeModalProps> = ({
    isOpen,
    initialStartDate,
    initialEndDate,
    onClose,
    onApply,
    onCancel,
}) => {
    const [dateRange, setDateRange] = useState([
        {
            startDate: initialStartDate || new Date(),
            endDate: initialEndDate || new Date(),
            key: "selection",
        },
    ]);

    // Update dateRange when initial props change (for saved preferences)
    useEffect(() => {
        if (initialStartDate && initialEndDate) {
            setDateRange([
                {
                    startDate: initialStartDate,
                    endDate: initialEndDate,
                    key: "selection",
                },
            ]);
        }
    }, [initialStartDate, initialEndDate]);

    const handleDateRangeChange = useCallback((ranges: any) => {
        setDateRange([ranges.selection]);
    }, []);

    const handleApply = useCallback(() => {
        const selection = dateRange[0];
        if (selection.startDate && selection.endDate) {
            onApply(selection.startDate as Date, selection.endDate as Date);
        }
        onClose();
    }, [dateRange, onApply, onClose]);

    const handleCancel = useCallback(() => {
        // Reset to initial values
        setDateRange([
            {
                startDate: initialStartDate || new Date(),
                endDate: initialEndDate || new Date(),
                key: "selection",
            },
        ]);
        if (onCancel) {
            onCancel();
        } else {
            onClose();
        }
    }, [initialStartDate, initialEndDate, onClose, onCancel]);

    return (
        <ModalDefault isOpen={isOpen}>
            <div className="glu-modal-content">
                <div className="glu-modal__header">
                    <div className="glu-modal__title glu-flex-grow">Set a date range</div>
                    <div className="glu-flex-shrink">
                        <button
                            aria-label="close"
                            type="button"
                            className="glu-btn-link g-icon g-icon-close"
                            onClick={handleCancel}
                        />
                    </div>
                </div>
                <div className="glu-modal__body">
                    <div className="custom-date-range-modal">
                        <DateRange
                            ranges={dateRange}
                            onChange={handleDateRangeChange}
                            moveRangeOnFirstSelection={false}
                            months={1}
                            direction="horizontal"
                        />
                    </div>
                </div>
                <footer className="glu-modal__footer">
                    <div className="glu-flex-grow glu-flex-row">
                        <div className="glu-flex-grow text-right">
                            <button className="glu-btn glu-btn--secondary" onClick={handleCancel}>
                                Cancel
                            </button>
                        </div>
                        <div className="glu-flex-shrink">
                            <button className="glu-btn glu-btn--primary" onClick={handleApply}>
                                Apply
                            </button>
                        </div>
                    </div>
                </footer>
            </div>
        </ModalDefault>
    );
};

export default CustomDateRangeModal;
